export function timeToMinutes(value) {
  if (!value || typeof value !== "string") return null;

  const m = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;

  const hh = Number(m[1]);
  const mm = Number(m[2]);

  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;

  return hh * 60 + mm;
}

export function minutesToTime(minutes) {
  if (!Number.isFinite(minutes)) return "";
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

export function overlaps(startA, endA, startB, endB) {
  return startA < endB && startB < endA;
}

function normalizeTemplateRole(roleLabel = "") {
  const raw = String(roleLabel || "").trim();

  if (/^Physician(?:\s+|__)\d+$/i.test(raw)) return "Physician";
  if (/^APRN\/PA(?:\s+|__)\d+$/i.test(raw)) return "APRN/PA";
  if (/^RN(?:\s+|__)\d+$/i.test(raw)) return "RN";
  if (/^Paramedic(?:\s+|__)\d+$/i.test(raw)) return "Paramedic";
  if (/^EMT\/AEMT(?:\s+|__)\d+$/i.test(raw)) return "EMT/AEMT";
  if (/^Ambulance(?:\s+|__)\d+$/i.test(raw)) return "Ambulance";
  if (/^Ambulance\s*\(ALS\)(?:\s+|__)\d+$/i.test(raw)) return "Ambulance";
  if (/^Ambulance\s*\(BLS\)(?:\s+|__)\d+$/i.test(raw)) return "Ambulance";

  const tentFromDisplay = raw.match(/^(Tent\s+\d+)\s*-\s*Slot\s+\d+/i);
  if (tentFromDisplay) return tentFromDisplay[1];

  const tentFromKey = raw.match(/^(Tent\s+\d+)__(\d+)$/i);
  if (tentFromKey) return tentFromKey[1];

  const bikeChild = raw.match(/^(Bike\s+\d+)__(paramedic|partner)$/i);
  if (bikeChild) return bikeChild[1];

  const cartChild = raw.match(/^(Cart\s+\d+)__(paramedic|partner)$/i);
  if (cartChild) return cartChild[1];

  return raw;
}

export function getRoleDefaultTimes(roleLabel = "") {
  const role = normalizeTemplateRole(roleLabel);

  // Command Team
  if (
    role === "Incident Command" ||
    role === "Unified Command" ||
    role === "Planning" ||
    role === "Logistics"
  ) {
    return { start: "05:00", end: "19:00" };
  }

  // Supervisors + Dispatch
  if (
    role === "Ops Manager" ||
    /^Ops Supervisor\s+\d+$/i.test(role) ||
    role === "Dispatcher" ||
    role === "Dispatcher 2" ||
    role === "Triage Supervisor"
  ) {
    return { start: "05:30", end: "19:00" };
  }

  // Everyone else
  return { start: "06:00", end: "19:00" };
}

export function getSlotTemplateRange(roleLabel = "") {
  const tt = getRoleDefaultTimes(roleLabel);
  return {
    start: tt.start,
    end: tt.end,
    startMin: timeToMinutes(tt.start),
    endMin: timeToMinutes(tt.end),
  };
}

export function sortAssignments(list = []) {
  return [...list].sort((a, b) => {
    const aStart = timeToMinutes(a?.start);
    const bStart = timeToMinutes(b?.start);

    if (aStart == null && bStart == null) return 0;
    if (aStart == null) return 1;
    if (bStart == null) return -1;
    if (aStart !== bStart) return aStart - bStart;

    const aEnd = timeToMinutes(a?.end);
    const bEnd = timeToMinutes(b?.end);

    if (aEnd == null && bEnd == null) return 0;
    if (aEnd == null) return 1;
    if (bEnd == null) return -1;
    if (aEnd !== bEnd) return aEnd - bEnd;

    return String(a?.assignmentId || "").localeCompare(
      String(b?.assignmentId || "")
    );
  });
}

export function hoursBetweenTimes(start, end) {
  const s = timeToMinutes(start);
  const e = timeToMinutes(end);
  if (s == null || e == null || e <= s) return 0;
  return (e - s) / 60;
}

export function getOpenGaps(assignments = [], slotStart = "06:00", slotEnd = "19:00") {
  const startMin = timeToMinutes(slotStart);
  const endMin = timeToMinutes(slotEnd);

  if (startMin == null || endMin == null || endMin <= startMin) return [];

  const sorted = sortAssignments(assignments)
    .map((a) => ({
      startMin: timeToMinutes(a?.start),
      endMin: timeToMinutes(a?.end),
    }))
    .filter((a) => a.startMin != null && a.endMin != null && a.endMin > a.startMin)
    .sort((a, b) => a.startMin - b.startMin);

  const gaps = [];
  let cursor = startMin;

  for (const item of sorted) {
    const s = Math.max(item.startMin, startMin);
    const e = Math.min(item.endMin, endMin);

    if (s > cursor) {
      gaps.push({
        start: minutesToTime(cursor),
        end: minutesToTime(s),
        startMin: cursor,
        endMin: s,
      });
    }

    if (e > cursor) {
      cursor = e;
    }
  }

  if (cursor < endMin) {
    gaps.push({
      start: minutesToTime(cursor),
      end: minutesToTime(endMin),
      startMin: cursor,
      endMin: endMin,
    });
  }

  return gaps;
}

export function findBestOpenRange(assignments = [], slotStart = "06:00", slotEnd = "19:00") {
  const gaps = getOpenGaps(assignments, slotStart, slotEnd);

  if (!gaps.length) {
    return {
      start: slotStart,
      end: slotEnd,
    };
  }

  let best = gaps[0];
  let bestDur = best.endMin - best.startMin;

  for (const gap of gaps.slice(1)) {
    const dur = gap.endMin - gap.startMin;
    if (dur > bestDur) {
      best = gap;
      bestDur = dur;
    }
  }

  return {
    start: best.start,
    end: best.end,
  };
}